/**
 * Couche de gestes: souris et doigt, sans mode a basculer.
 *
 *   tap court                -> deplacement (tap-to-move)
 *   appui maintenu / glisse  -> joystick virtuel a l'endroit de l'appui
 *   deux doigts              -> pincer (zoom) + torsion (pivot)
 *   molette                  -> zoom
 *   bouton droit ou Maj      -> pivot
 *
 * Toutes les interactions passent par journal.input. Les evenements continus
 * (joystick, pincement, molette) sont limites a une ligne par ~250 ms.
 */
import { CAM_ELEVATION } from '../../shared/constants.ts'
import { journal } from '../log/journal.ts'

export interface EcouteursPointeur {
  tap(clientX: number, clientY: number): void
  joystickDebut(clientX: number, clientY: number): void
  /**
   * direction deja dans le repere du monde, normalisee; intensite 0..1.
   *
   * `ecranX` et `ecranY` sont l'ecart BRUT du doigt depuis le point d'appui, en
   * pixels: c'est ce qu'il faut pour dessiner le pouce du joystick sous le
   * doigt. La direction monde, elle, a subi la compensation de la vue a 45
   * degres et ne peut pas servir a placer quoi que ce soit a l'ecran.
   */
  joystickBouge(dirX: number, dirZ: number, intensite: number, ecranX: number, ecranY: number): void
  joystickFin(): void
  pivot(deltaRadians: number): void
  zoom(facteur: number): void
}

/** Duree maximale d'un tap, en ms. Au-dela, l'appui devient un joystick. */
const SEUIL_TAP_MS = 220

/** Deplacement maximal d'un tap, en pixels. */
const SEUIL_TAP_PX = 12

/** Rayon de saturation du joystick, en pixels (intensite = 1). */
const RAYON_JOYSTICK_PX = 60

/** En deca, le pouce du joystick ne pousse rien. */
const ZONE_MORTE_PX = 10

/** Une ligne de journal au maximum par tranche, pour les gestes continus. */
const LOG_MS = 250

/**
 * Le joystick, lui, se journalise beaucoup plus rarement.
 *
 * Une ligne toutes les 250 ms tant que le doigt appuie, ca faisait 456 lignes sur
 * les 960 d'une session: presque la moitie du fichier en echantillons de
 * joystick, et le reste noye dedans. Or ces echantillons ne servent pas un par
 * un — ce qu'on veut savoir, c'est « il a pousse vers l'ouest pendant six
 * secondes ». Un point de temps en temps pour les longues poussees, et un resume
 * au relachement.
 */
const LOG_MS_JOYSTICK = 3000

/** Sensibilite du pivot au glissement horizontal, en radians par pixel. */
const PIVOT_PAR_PX = 0.008

/** Sensibilite du zoom a la molette, par unite de deltaY. */
const ZOOM_PAR_DELTA = 0.0012

/**
 * La vue a 45 degres ecrase l'axe vertical de l'ecran d'un facteur
 * sin(elevation). On divise la composante verticale du geste par ce facteur
 * pour que le bonhomme parte exactement dans la direction du doigt a l'ecran.
 */
const COMPENSATION_VERTICALE = 1 / Math.sin(CAM_ELEVATION)

type Mode = 'aucun' | 'attente' | 'joystick' | 'pivot' | 'deuxDoigts'

interface Doigt {
  id: number
  x0: number
  y0: number
  x: number
  y: number
  t0: number
  /** Vrai des que le seuil de deplacement est depasse. */
  bouge: boolean
}

/** Ecart d'angle ramene dans (-PI, PI]. */
function angleCourt(a: number): number {
  let x = a % (Math.PI * 2)
  if (x > Math.PI) x -= Math.PI * 2
  if (x <= -Math.PI) x += Math.PI * 2
  return x
}

export class Pointeur {
  private readonly cible: HTMLElement
  private readonly ecouteurs: EcouteursPointeur
  private readonly azimut: () => number

  private readonly doigts = new Map<number, Doigt>()
  private mode: Mode = 'aucun'
  private idPrincipal: number | null = null
  private minuterie: number | null = null

  /** Reference du geste a deux doigts. */
  private refDistance = 0
  private refAngle = 0

  /** Derniere abscisse utilisee pour le pivot a la souris. */
  private pivotX = 0

  private readonly derniersLogs = new Map<string, number>()
  /** Cumul de la poussee de joystick en cours, resume au relachement. */
  private cumulJoystick = { n: 0, x: 0, z: 0, i: 0, t0: 0 }
  private detruit = false

  constructor(cible: HTMLElement, ecouteurs: EcouteursPointeur, azimutCourant: () => number) {
    this.cible = cible
    this.ecouteurs = ecouteurs
    this.azimut = azimutCourant

    this.cible.addEventListener('pointerdown', this.surAppui)
    this.cible.addEventListener('pointermove', this.surDeplacement)
    this.cible.addEventListener('pointerup', this.surRelachement)
    this.cible.addEventListener('pointercancel', this.surAnnulation)
    this.cible.addEventListener('wheel', this.surMolette, { passive: false })
    this.cible.addEventListener('contextmenu', this.surMenu)
    window.addEventListener('blur', this.surPerteFocus)
    document.addEventListener('visibilitychange', this.surVisibilite)
  }

  /** Vrai si un joystick est en cours. */
  get actif(): boolean {
    return this.mode === 'joystick'
  }

  detruire(): void {
    if (this.detruit) return
    this.detruit = true
    this.toutAnnuler('destruction')
    this.cible.removeEventListener('pointerdown', this.surAppui)
    this.cible.removeEventListener('pointermove', this.surDeplacement)
    this.cible.removeEventListener('pointerup', this.surRelachement)
    this.cible.removeEventListener('pointercancel', this.surAnnulation)
    this.cible.removeEventListener('wheel', this.surMolette)
    this.cible.removeEventListener('contextmenu', this.surMenu)
    window.removeEventListener('blur', this.surPerteFocus)
    document.removeEventListener('visibilitychange', this.surVisibilite)
  }

  // ------------------------------------------------------------- evenements

  private readonly surAppui = (e: PointerEvent): void => {
    const cible = e.target
    // Un appui qui commence sur l'interface ne pilote pas la camera.
    if (cible instanceof Element && cible.closest('#interface *')) return

    try {
      this.cible.setPointerCapture(e.pointerId)
    } catch {
      // Certains navigateurs refusent la capture d'un pointeur deja parti.
    }

    const d: Doigt = {
      id: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      x: e.clientX,
      y: e.clientY,
      t0: performance.now(),
      bouge: false,
    }
    this.doigts.set(e.pointerId, d)

    // Souris: bouton droit ou Maj -> pivot direct.
    if (e.pointerType !== 'touch' && (e.button === 2 || e.shiftKey)) {
      e.preventDefault()
      this.arreterMinuterie()
      this.finirJoystick('pivot souris')
      this.mode = 'pivot'
      this.idPrincipal = e.pointerId
      this.pivotX = e.clientX
      journal.input('pivot', 'debut de pivot a la souris', { x: Math.round(e.clientX) })
      return
    }

    /*
     * UN POUCE SUR LE JOYSTICK N'EMPECHE PAS DE TOUCHER AILLEURS. Tout second
     * doigt basculait en pincement: un pouce pose avalait chaque tap de l'autre
     * main, et zoomer en marchant coupait la marche. Pendant le joystick, le
     * second doigt est un TAP (traite au relachement, voir `terminer`).
     */
    if (this.doigts.size >= 2 && this.mode === 'joystick') return

    if (this.doigts.size >= 2) {
      this.arreterMinuterie()
      this.finirJoystick('deux doigts')
      this.mode = 'deuxDoigts'
      this.majReferenceDeuxDoigts()
      journal.input('pincer', 'debut du geste a deux doigts')
      return
    }

    this.mode = 'attente'
    this.idPrincipal = e.pointerId
    this.arreterMinuterie()
    this.minuterie = window.setTimeout(() => {
      this.minuterie = null
      this.promouvoirJoystick()
    }, SEUIL_TAP_MS)
  }

  private readonly surDeplacement = (e: PointerEvent): void => {
    const d = this.doigts.get(e.pointerId)
    if (!d) return
    d.x = e.clientX
    d.y = e.clientY
    if (!d.bouge) {
      const dx = d.x - d.x0
      const dy = d.y - d.y0
      if (dx * dx + dy * dy > SEUIL_TAP_PX * SEUIL_TAP_PX) d.bouge = true
    }

    if (this.mode === 'deuxDoigts') {
      this.majDeuxDoigts()
      return
    }

    if (this.mode === 'pivot') {
      if (e.pointerId !== this.idPrincipal) return
      const dx = e.clientX - this.pivotX
      this.pivotX = e.clientX
      if (dx !== 0) {
        // Glisser vers la droite fait tourner la scene vers la droite.
        this.ecouteurs.pivot(-dx * PIVOT_PAR_PX)
        this.logLimite('pivot', 'pivot en cours', { dx: Math.round(dx) })
      }
      return
    }

    if (this.mode === 'attente' && e.pointerId === this.idPrincipal) {
      // Maj enfoncee en cours de glissement: on passe au pivot.
      if (e.pointerType !== 'touch' && e.shiftKey && d.bouge) {
        this.arreterMinuterie()
        this.mode = 'pivot'
        this.pivotX = e.clientX
        journal.input('pivot', 'debut de pivot (Maj)', { x: Math.round(e.clientX) })
        return
      }
      if (d.bouge) this.promouvoirJoystick()
    }

    if (this.mode === 'joystick' && e.pointerId === this.idPrincipal) {
      this.emettreJoystick(d)
    }
  }

  private readonly surRelachement = (e: PointerEvent): void => {
    this.terminer(e, true)
  }

  private readonly surAnnulation = (e: PointerEvent): void => {
    this.terminer(e, false)
  }

  private readonly surMolette = (e: WheelEvent): void => {
    const cible = e.target
    if (cible instanceof Element && cible.closest('#interface *')) return
    e.preventDefault()
    // deltaMode 1 = lignes, 2 = pages.
    const echelle = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1
    let delta = e.deltaY * echelle
    if (delta > 240) delta = 240
    else if (delta < -240) delta = -240
    if (delta === 0) return
    const facteur = Math.exp(delta * ZOOM_PAR_DELTA)
    this.ecouteurs.zoom(facteur)
    this.logLimite('zoom', 'zoom a la molette', { facteur: Number(facteur.toFixed(3)) })
  }

  private readonly surMenu = (e: Event): void => {
    e.preventDefault()
  }

  private readonly surPerteFocus = (): void => {
    this.toutAnnuler('perte de focus')
  }

  private readonly surVisibilite = (): void => {
    if (document.hidden) this.toutAnnuler('onglet masque')
  }

  // ------------------------------------------------------------- interne

  private terminer(e: PointerEvent, autoriserTap: boolean): void {
    const d = this.doigts.get(e.pointerId)
    this.doigts.delete(e.pointerId)
    try {
      this.cible.releasePointerCapture(e.pointerId)
    } catch {
      // Le pointeur n'etait plus capture: rien a faire.
    }
    if (!d) return

    const duree = performance.now() - d.t0

    // Le second doigt pendant le joystick: un tap bref et immobile vaut un tap.
    if (this.mode === 'joystick' && e.pointerId !== this.idPrincipal) {
      if (autoriserTap && duree <= SEUIL_TAP_MS && !d.bouge) {
        journal.input('tap', 'tap d un second doigt pendant le joystick', {
          x: Math.round(d.x),
          y: Math.round(d.y),
          ms: Math.round(duree),
        })
        this.ecouteurs.tap(d.x, d.y)
      }
      return
    }

    if (this.mode === 'joystick' && e.pointerId === this.idPrincipal) {
      /*
       * Un tap LENT reste un tap.
       *
       * Au doigt, viser un point precis prend souvent plus des 220 ms du seuil:
       * on pose, on regarde ou on a pose, on releve. Le joystick s'ouvrait alors
       * sous le doigt immobile, et le relachement ne declenchait rien du tout.
       * Un appui sur trois ne faisait rien, sans que rien n'explique pourquoi.
       * Si le doigt n'a jamais bouge et n'a donc jamais rien pousse, ce que le
       * joueur a fait est un tap: on l'honore.
       */
      const immobile = !d.bouge
      this.finirJoystick(autoriserTap ? 'relachement' : 'annulation')
      this.mode = 'aucun'
      if (autoriserTap && immobile) {
        journal.input('tap', 'tap lent sur la scene', {
          x: Math.round(d.x),
          y: Math.round(d.y),
          type: e.pointerType,
          ms: Math.round(duree),
        })
        this.ecouteurs.tap(d.x, d.y)
      }
    } else if (this.mode === 'attente' && e.pointerId === this.idPrincipal) {
      this.arreterMinuterie()
      if (autoriserTap && duree <= SEUIL_TAP_MS && !d.bouge) {
        journal.input('tap', 'tap sur la scene', {
          x: Math.round(d.x),
          y: Math.round(d.y),
          type: e.pointerType,
          ms: Math.round(duree),
        })
        this.ecouteurs.tap(d.x, d.y)
      } else {
        journal.input('tap', 'appui abandonne', { ms: Math.round(duree), bouge: d.bouge })
      }
      this.mode = 'aucun'
    } else if (this.mode === 'pivot' && e.pointerId === this.idPrincipal) {
      journal.input('pivot', 'fin de pivot')
      this.mode = 'aucun'
    } else if (this.mode === 'deuxDoigts' && this.doigts.size < 2) {
      journal.input('pincer', 'fin du geste a deux doigts')
      this.mode = 'aucun'
      this.refDistance = 0
      /*
       * LE DOIGT QUI RESTE APRES UN PINCEMENT N'EST PAS MORT. Il ne pilotait
       * plus rien jusqu'a ce qu'on le leve. Il repart comme un appui neuf, de
       * la ou il est — mais deja « bouge »: le lever ne fera pas un tap.
       */
      const reste = [...this.doigts.values()][0]
      if (reste) {
        reste.x0 = reste.x
        reste.y0 = reste.y
        reste.bouge = true
        this.idPrincipal = reste.id
        this.mode = 'attente'
        this.arreterMinuterie()
        this.minuterie = window.setTimeout(() => {
          this.minuterie = null
          this.promouvoirJoystick()
        }, SEUIL_TAP_MS)
      }
    } else if (this.mode === 'deuxDoigts') {
      // Trois doigts, on en leve un: la paire change, la reference aussi —
      // sinon le zoom saute de l'ancien ecart au nouveau.
      this.majReferenceDeuxDoigts()
    }

    if (this.doigts.size === 0) {
      this.mode = 'aucun'
      this.idPrincipal = null
      this.arreterMinuterie()
    }
  }

  private promouvoirJoystick(): void {
    if (this.mode !== 'attente' || this.idPrincipal === null) return
    const d = this.doigts.get(this.idPrincipal)
    if (!d) return
    this.mode = 'joystick'
    this.cumulJoystick = { n: 0, x: 0, z: 0, i: 0, t0: performance.now() }
    this.derniersLogs.delete('joystick')
    this.ecouteurs.joystickDebut(d.x0, d.y0)
    journal.input('joystick', 'debut du joystick', { x: Math.round(d.x0), y: Math.round(d.y0) })
    this.emettreJoystick(d)
  }

  private finirJoystick(raison: string): void {
    if (this.mode !== 'joystick') return
    this.mode = 'aucun'
    this.ecouteurs.joystickFin()
    // Le resume vaut mieux que les echantillons: la direction MOYENNE et la duree
    // disent ce que le joueur a voulu faire, une suite de vecteurs ne le dit pas.
    const c = this.cumulJoystick
    const n = Math.max(1, c.n)
    journal.input('joystick', `fin de poussee (${raison})`, {
      secondes: Number(((performance.now() - c.t0) / 1000).toFixed(1)),
      capX: Number((c.x / n).toFixed(2)),
      capZ: Number((c.z / n).toFixed(2)),
      intensiteMoyenne: Number((c.i / n).toFixed(2)),
      echantillons: c.n,
    })
  }

  private emettreJoystick(d: Doigt): void {
    const dx = d.x - d.x0
    const dy = d.y - d.y0
    const longueur = Math.sqrt(dx * dx + dy * dy)
    // ZONE MORTE. Un pouce qui repose tremble d'un ou deux pixels: a 0,5 px,
    // ca suffisait a lancer le bonhomme au pas de course dans une direction
    // au hasard (toute poussee est une course, voir `intentionDe`).
    if (longueur < ZONE_MORTE_PX) {
      this.ecouteurs.joystickBouge(0, 0, 0, 0, 0)
      return
    }
    const intensite = longueur > RAYON_JOYSTICK_PX ? 1 : longueur / RAYON_JOYSTICK_PX

    // Ecran -> monde. Avec la camera a l'azimut a, dans le plan (x, z):
    //   droite de l'ecran = ( sin a, -cos a)
    //   haut de l'ecran   = (-cos a, -sin a)
    // La composante verticale est d'abord dilatee pour annuler l'ecrasement de
    // la vue a 45 degres: le bonhomme part alors pile dans la direction du
    // doigt tel qu'on le voit a l'ecran, quel que soit le pivot de la camera.
    const ex = dx
    const ey = dy * COMPENSATION_VERTICALE
    const norme = Math.sqrt(ex * ex + ey * ey)
    const nx = ex / norme
    const ny = ey / norme

    const a = this.azimut()
    const sa = Math.sin(a)
    const ca = Math.cos(a)
    const wx = nx * sa + ny * ca
    const wz = -nx * ca + ny * sa

    this.ecouteurs.joystickBouge(wx, wz, intensite, dx, dy)
    this.cumulJoystick.n++
    this.cumulJoystick.x += wx
    this.cumulJoystick.z += wz
    this.cumulJoystick.i += intensite
    this.logLimite(
      'joystick',
      'poussee en cours',
      { x: Number(wx.toFixed(2)), z: Number(wz.toFixed(2)), i: Number(intensite.toFixed(2)) },
      LOG_MS_JOYSTICK,
    )
  }

  private deuxPremiers(): [Doigt, Doigt] | null {
    let a: Doigt | null = null
    let b: Doigt | null = null
    for (const d of this.doigts.values()) {
      if (!a) a = d
      else if (!b) {
        b = d
        break
      }
    }
    return a && b ? [a, b] : null
  }

  private majReferenceDeuxDoigts(): void {
    const paire = this.deuxPremiers()
    if (!paire) return
    const [a, b] = paire
    this.refDistance = Math.hypot(b.x - a.x, b.y - a.y)
    this.refAngle = Math.atan2(b.y - a.y, b.x - a.x)
  }

  private majDeuxDoigts(): void {
    const paire = this.deuxPremiers()
    if (!paire) return
    const [a, b] = paire
    const distance = Math.hypot(b.x - a.x, b.y - a.y)
    const angle = Math.atan2(b.y - a.y, b.x - a.x)

    if (this.refDistance > 8 && distance > 8) {
      // Ecarter les doigts rapproche la camera: le demi-frustum diminue.
      const facteur = this.refDistance / distance
      if (Math.abs(facteur - 1) > 1e-3) {
        this.ecouteurs.zoom(facteur)
        this.logLimite('zoom', 'pincement', { facteur: Number(facteur.toFixed(3)) })
      }
      const dAngle = angleCourt(angle - this.refAngle)
      if (Math.abs(dAngle) > 1e-3) {
        // Torsion dans le sens des aiguilles = scene qui suit les doigts.
        this.ecouteurs.pivot(-dAngle)
        this.logLimite('pivot', 'torsion a deux doigts', { deg: Math.round((dAngle * 180) / Math.PI) })
      }
    }

    this.refDistance = distance
    this.refAngle = angle
  }

  private toutAnnuler(raison: string): void {
    this.arreterMinuterie()
    if (this.mode === 'joystick') this.finirJoystick(raison)
    else if (this.mode !== 'aucun') journal.input('geste', `geste interrompu (${raison})`)
    for (const id of this.doigts.keys()) {
      try {
        this.cible.releasePointerCapture(id)
      } catch {
        // Deja relache.
      }
    }
    this.doigts.clear()
    this.mode = 'aucun'
    this.idPrincipal = null
    this.refDistance = 0
  }

  private arreterMinuterie(): void {
    if (this.minuterie !== null) {
      clearTimeout(this.minuterie)
      this.minuterie = null
    }
  }

  private logLimite(tag: string, msg: string, data?: unknown, tranche = LOG_MS): void {
    const t = performance.now()
    const precedent = this.derniersLogs.get(tag) ?? -Infinity
    if (t - precedent < tranche) return
    this.derniersLogs.set(tag, t)
    journal.input(tag, msg, data)
  }
}
